import { FunctionComponent, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  ActionButton,
  CommandBar,
  ICommandBarItemProps,
  Icon,
  Label,
  List,
  Panel,
  PanelType,
  registerIcons,
  Stack,
  Text,
  TextField,
} from '@fluentui/react';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { IField, IMetadata } from '../../services/metadataService';
import React from 'react';
import FieldEditorV2 from './FieldEditorV2';
import {
  makeStyles,
  Checkbox,
  Dropdown,
  Field,
  Input,
  Option,
  Radio,
  RadioGroup,
  Tab,
  TabList,
  type SelectTabData,
  type SelectTabEvent,
  tokens,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
  Button,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { FileViewerField } from '@moko/ui';
import { allowsImageAndVideo } from '@moko/utils';

export interface IFieldsEditorV2Props {
  metadata?: IMetadata;
  setMetadata: (metadata: IMetadata) => void;
  setMetadataStr: (metadataStr: string) => void;
  references?: string[];
  entityType: string;
  inPreviewMode?: boolean;
  setInPreviewMode?: (inPreview: boolean) => void;
  currFieldIndex?: number;
  setCurrFieldIndex: (index?: number) => void;
}

export const NumberWithUnitIcon: React.FC<React.SVGProps<SVGSVGElement>> = (
  props
) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 40 20"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <rect
      x="1"
      y="1"
      width="25"
      height="18"
      stroke="currentColor"
      fill="none"
    />
    <text
      x="4"
      y="14"
      fontSize="12"
      fontFamily="Segoe UI"
      fill="currentColor"
      fontStyle="normal"
      fontWeight="bold"
    >
      123
    </text>
    <rect
      x="28"
      y="1"
      width="11"
      height="18"
      stroke="currentColor"
      fill="none"
    />
    <polygon points="30,8 36,8 33,12" fill="currentColor" />
  </svg>
);

registerIcons({
  icons: {
    NumberWithUnit: <NumberWithUnitIcon />,
  },
});

function Draggable(props: any) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: props.id,
  });
  const style: React.CSSProperties = {
    // Outputs `translate3d(x, y, 0)`
    transform: CSS.Translate.toString(transform),
    // ensure dragged element is above dropping areas while dragging
    zIndex: 1000,
  };

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {props.children}
    </div>
  );
}

export function Droppable(props: any) {
  const { isOver, setNodeRef } = useDroppable({
    id: props.id,
  });
  const style = {
    opacity: isOver ? 1 : 0.5,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {props.children}
    </div>
  );
}

export const MOVE_DRAG_PREFIX = 'move:';

function ReorderHandle({
  fieldName,
  className,
}: {
  fieldName: string;
  className: string;
}) {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `${MOVE_DRAG_PREFIX}${fieldName}`,
  });
  return (
    <button
      ref={setNodeRef}
      type="button"
      title="Reorder"
      aria-label="Reorder"
      className={className}
      {...listeners}
      {...attributes}
    >
      <Icon iconName="Move" style={{ fontSize: 16 }} />
    </button>
  );
}

// makeStyles definition (keeps styles in code like FluentUI samples)
const useStyles = makeStyles({
  canvas: {
    height: '475px',
    width: '500px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    padding: '0px 4px 4px',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    background: tokens.colorNeutralBackground1,
    borderRadius: '6px',
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
  },
  canvasInner: {
    flex: 1,
    overflowY: 'auto',
    scrollbarGutter: 'stable',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '4px 2px',
  },
  droppingArea: {
    width: '100%',
    height: '20px',
    background: tokens.colorNeutralBackground3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    borderRadius: '4px',
    margin: '2px 0',
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
  },
  panelContainer: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    padding: '2px',
    borderRadius: '6px',
    background: 'transparent',
    boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.02)',
    color: tokens.colorBrandBackgroundInvertedSelected,
  },
  panelHeader: {
    margin: '-2px -2px 8px -2px',
    padding: '4px 8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    borderRadius: '6px 6px 0 0',
    fontWeight: 600,
    background: tokens.colorBrandBackgroundInvertedSelected,
  },
  fieldPreview: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: '8px',
    borderRadius: '6px',
    background: tokens.colorNeutralBackground1,
  },
  actionBar: {
    position: 'absolute',
    top: '6px',
    right: '6px',
    zIndex: 2,
  },
  // Hover is driven purely by CSS so it can never get "stuck" (the browser
  // recomputes :hover from the real cursor position every frame, unlike manual
  // mouseover/out state which can miss events during fast moves or drags).
  // Wrappers carry a stable `data-field-wrapper` attribute so the rules below
  // can reference them regardless of Griffel's hashed class names.
  fieldWrapper: {
    position: 'relative',
    marginBottom: '2px',
    // Default: hide this wrapper's own toolbar.
    '& > [data-field-toolbar]': {
      opacity: 0,
      pointerEvents: 'none',
    },

    '&:hover > [data-field-toolbar]': {
      opacity: 1,
      pointerEvents: 'auto',
    },

    '&:has([data-field-wrapper]:hover) > [data-field-toolbar]': {
      opacity: 0,
      pointerEvents: 'none',
    },
  },
  fieldToolbar: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    background: tokens.colorNeutralBackground1,
    borderRadius: '4px',
    boxShadow: tokens.shadow4,
  },
  reorderHandle: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Match the v8 CommandBar icon-button box so the three buttons sit flush.
    height: '28px',
    width: '40px',
    padding: 0,
    background: 'transparent',
    border: 'none',
    cursor: 'grab',
    touchAction: 'none',
    // Same icon color as the CommandBar buttons it sits next to.
    color: tokens.colorNeutralForeground1,
    ':hover': {
      background: tokens.colorNeutralBackground1Hover,
    },
  },
});

// control preview components are created inside the component now
const FieldWrapper = ({
  children,
  fieldName,
  onProperties,
  onDelete,
  isPanel,
  onExpand,
  onCollapse,
  isCollapsed,
}: {
  children: React.ReactNode;
  fieldName: string;
  onProperties: () => void;
  onDelete: () => void;
  isPanel: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  isCollapsed: boolean;
}) => {
  const classes = useStyles();

  const expandCollapseItem: ICommandBarItemProps[] = isPanel
    ? [
        isCollapsed
          ? {
              key: 'expand',
              text: '',
              iconOnly: true,
              iconProps: {
                iconName: 'MaximumValue',
                styles: { root: { transform: 'rotate(90deg)' } },
              },
              title: 'Expand',
              onClick: onExpand,
            }
          : {
              key: 'collapse',
              text: '',
              iconOnly: true,
              iconProps: {
                iconName: 'MinimumValue',
                styles: { root: { transform: 'rotate(270deg)' } },
              },
              title: 'Collapse',
              onClick: onCollapse,
            },
      ]
    : [];

  const commonItems: ICommandBarItemProps[] = [
    {
      key: 'reorder',
      text: '',
      iconOnly: true,
      title: 'Reorder',
      // Rendered as a dnd-kit drag handle but sized/spaced like the other
      // CommandBar buttons so all three read as one toolbar group.
      onRender: () => (
        <ReorderHandle
          fieldName={fieldName}
          className={classes.reorderHandle}
        />
      ),
    },
    {
      key: 'properties',
      text: '',
      iconOnly: true,
      iconProps: { iconName: 'Settings' },
      title: 'Properties',
      onClick: onProperties,
    },
    {
      key: 'delete',
      text: '',
      iconOnly: true,
      iconProps: { iconName: 'Delete' },
      title: 'Delete',
      onClick: onDelete,
    },
  ];
  const items = [...commonItems, ...expandCollapseItem];
  const barWidth = items.length * 40;
  return (
    <div className={classes.fieldWrapper} data-field-wrapper>
      <div className={classes.fieldToolbar} data-field-toolbar>
        <CommandBar
          items={items}
          styles={{
            root: {
              background: 'transparent',
              height: 28,
              padding: 0,
              width: barWidth,
              minWidth: barWidth,
            },
            primarySet: { minWidth: barWidth },
          }}
        />
      </div>
      {children}
    </div>
  );
};

// control components (Panel and control map) will be created inside the component so they can use hook classes

const FieldsEditorV2: FunctionComponent<IFieldsEditorV2Props> = (
  props: IFieldsEditorV2Props
) => {
  const {
    metadata,
    setMetadata,
    setMetadataStr,
    references,
    inPreviewMode = false,
    setInPreviewMode,
    currFieldIndex,
    setCurrFieldIndex,
  } = props;
  const [items, setItems] = useState<IField[]>(metadata?.fields || []);
  const [activeId, setActiveId] = useState<string | null>(null);
  const hasTabs = (metadata?.tabs?.length ?? 0) > 0;
  const [selectedTab, setSelectedTab] = useState<string>(
    metadata?.tabs?.[0]?.tabName ?? ''
  );

  React.useEffect(() => {
    setItems(metadata?.fields || []);
  }, [metadata?.fields]);

  React.useEffect(() => {
    if (!hasTabs) {
      if (selectedTab !== '') {
        setSelectedTab('');
      }
      return;
    }

    const tabs = metadata?.tabs ?? [];
    if (!tabs.some((tab) => tab.tabName === selectedTab)) {
      setSelectedTab(tabs[0]?.tabName ?? '');
    }
  }, [hasTabs, metadata?.tabs, selectedTab]);

  const classes = useStyles();

  const controlWrapperStyle: React.CSSProperties = {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: 8,
    marginLeft: 2,
    marginRight: 2,
    textAlign: 'left',
    borderRadius: 6,
    background: tokens.colorNeutralBackground1,
  };

  const renderFieldNameTag = (name?: string) => (
    <div
      style={{
        display: inPreviewMode ? 'none' : 'flex',
        justifyContent: 'flex-end',
        marginBottom: 4,
      }}
    >
      <code
        style={{
          display: inPreviewMode ? 'none' : 'block',
          fontFamily: 'Consolas, "Courier New", monospace',
          fontSize: 11,
          color: tokens.colorNeutralForeground3,
        }}
      >
        {name}
      </code>
    </div>
  );

  const TextFieldControl = (field: IField) => (
    <div style={controlWrapperStyle}>
      {renderFieldNameTag(field.name)}
      <Field
        label={`${field.label}`}
        orientation={field.labelAbove ? undefined : 'horizontal'}
      >
        <Input />
      </Field>
    </div>
  );
  const ContactControl = (field: IField) => (
    <div style={controlWrapperStyle}>
      {renderFieldNameTag(field.name)}
      {field.labelAbove ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              field.options?.showExtensionInContactField === true
                ? 'minmax(0, 2fr) minmax(0, 1fr)'
                : 'minmax(0, 1fr)',
            gap: 12,
            alignItems: 'end',
          }}
        >
          <Field label="Phone">
            <Input style={{ minWidth: 0 }} />
          </Field>
          {field.options?.showExtensionInContactField === true && (
            <Field label="Ext">
              <Input style={{ minWidth: 0 }} />
            </Field>
          )}
        </div>
      ) : (
        <Field label={`${field.label}`} orientation="horizontal">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                field.options?.showExtensionInContactField === true
                  ? 'minmax(0, 2fr) auto minmax(0, 1fr)'
                  : 'minmax(0, 1fr)',
              gap: 8,
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            <Input style={{ minWidth: 0 }} />
            {field.options?.showExtensionInContactField === true && (
              <>
                <Text>Ext</Text>
                <Input style={{ minWidth: 0 }} />
              </>
            )}
          </div>
        </Field>
      )}
    </div>
  );
  const NumberWithUnitControl = (field: IField) => (
    <div style={controlWrapperStyle}>
      {renderFieldNameTag(field.name)}
      <Field
        label={`${field.label}`}
        orientation={field.labelAbove ? undefined : 'horizontal'}
        style={{ width: '100%' }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 5,
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Dropdown
            placeholder="Select an option"
            style={{ flex: '0 1 220px', minWidth: 120, maxWidth: '60%' }}
          >
            {metadata?.lookups.find((l) => l.name === field.reference)
              ? metadata?.lookups
                  .find((l) => l.name === field.reference)
                  ?.values.map((opt) => (
                    <Option key={opt.key} value={opt.key}>
                      {opt.value}
                    </Option>
                  ))
              : [1, 2, 3].map((i) => (
                  <Option
                    key={`option${i}`}
                    value={`option${i}`}
                  >{`Option ${i}`}</Option>
                ))}
          </Dropdown>
          <Input style={{ flex: '1 1 auto', minWidth: 0, maxWidth: '100%' }} />
        </div>
      </Field>
      {metadata?.lookups.find((l) => l.name === field.reference) ? null : (
        <Text variant="xSmall">
          * Actual options would appear at runtime or after save and reload
        </Text>
      )}
    </div>
  );
  const DropdownControl = (field: IField) => (
    <div style={controlWrapperStyle}>
      {renderFieldNameTag(field.name)}
      <Field
        label={`${field.label}`}
        orientation={field.labelAbove ? undefined : 'horizontal'}
      >
        <Dropdown placeholder="Select an option">
          {metadata?.lookups.find((l) => l.name === field.reference)
            ? metadata?.lookups
                .find((l) => l.name === field.reference)
                ?.values.map((opt) => (
                  <Option key={opt.key} value={opt.key}>
                    {opt.value}
                  </Option>
                ))
            : [1, 2, 3].map((i) => (
                <Option
                  key={`option${i}`}
                  value={`option${i}`}
                >{`Option ${i}`}</Option>
              ))}
        </Dropdown>
      </Field>
      {metadata?.lookups.find((l) => l.name === field.reference) ? null : (
        <Text variant="xSmall">
          * Actual options would appear at runtime or after save and reload
        </Text>
      )}
    </div>
  );
  const DatePickerControl = (field: IField) => (
    <div style={controlWrapperStyle}>
      {renderFieldNameTag(field.name)}
      <Field
        label={`${field.label}`}
        orientation={field.labelAbove ? undefined : 'horizontal'}
      >
        <DatePicker />
      </Field>
    </div>
  );
  const AutoCompleteControl = (field: IField) => (
    <div style={controlWrapperStyle}>
      {renderFieldNameTag(field.name)}
      <Field
        label={`${field.label}`}
        orientation={field.labelAbove ? undefined : 'horizontal'}
      >
        <Input />
      </Field>
    </div>
  );
  const UserSelectorControl = (field: IField) => (
    <div style={controlWrapperStyle}>
      {renderFieldNameTag(field.name)}
      <Field
        label={`${field.label}`}
        orientation={field.labelAbove ? undefined : 'horizontal'}
      >
        <Input contentBefore={<Icon iconName="Contact" />} />
      </Field>
    </div>
  );
  const AddressControl = (field: IField) => (
    <div style={controlWrapperStyle}>
      {renderFieldNameTag(field.name)}
      <Field
        label={`${field.label}`}
        orientation={field.labelAbove ? undefined : 'horizontal'}
      >
        <Input contentBefore={<Icon iconName="MapPin" />} />
      </Field>
    </div>
  );
  const CoordinateControl = (field: IField) => (
    <div style={controlWrapperStyle}>
      {renderFieldNameTag(field.name)}
      <Field
        label={`${field.label}`}
        orientation={field.labelAbove ? undefined : 'horizontal'}
      >
        <TextField />
      </Field>
    </div>
  );
  const CheckboxControl = (field: IField) => (
    <div style={controlWrapperStyle}>
      {renderFieldNameTag(field.name)}
      <Field
        label={`${field.label}`}
        orientation={field.labelAbove ? undefined : 'horizontal'}
      >
        <Checkbox />
      </Field>
    </div>
  );
  const RadioButtonControl = (field: IField) => (
    <div style={controlWrapperStyle}>
      {renderFieldNameTag(field.name)}
      <Field
        label={`${field.label}`}
        orientation={field.labelAbove ? undefined : 'horizontal'}
      >
        <RadioGroup
          layout={
            field.optionsLayout === 'Horizontal'
              ? 'horizontal'
              : field.optionsLayout === 'HorizontalStacked'
              ? 'horizontal-stacked'
              : 'vertical'
          }
        >
          {metadata?.lookups.find((l) => l.name === field.reference)
            ? metadata?.lookups
                .find((l) => l.name === field.reference)
                ?.values.map((opt) => (
                  <Radio label={opt.value} value={opt.key} />
                ))
            : [1, 2, 3].map((i) => (
                <Radio label={`option${i}`} value={`option${i}`} />
              ))}
        </RadioGroup>
      </Field>
      {metadata?.lookups.find((l) => l.name === field.reference) ? null : (
        <Text variant="xSmall">
          * Actual options would appear at runtime or after save and reload
        </Text>
      )}
    </div>
  );
  const TextControl = (field: IField) => (
    <div style={controlWrapperStyle}>
      {renderFieldNameTag(field.name)}
      <Text>{`${field.label}`}</Text>
    </div>
  );

  const ImageControl = (field: IField) => (
    <div style={controlWrapperStyle}>
      {renderFieldNameTag(field.name)}
      <Text>{`${field.label}`}</Text>
      <Text
        variant="xSmall"
        style={{
          display: 'block',
          marginTop: 6,
          color: tokens.colorNeutralForeground3,
        }}
      >
        {allowsImageAndVideo(field.options)
          ? 'Supports: Image and video'
          : 'Supports: Image only'}
      </Text>
    </div>
  );

  const SignatureControl = (field: IField) => (
    <div style={controlWrapperStyle}>
      {renderFieldNameTag(field.name)}
      <Field
        label={`${field.label}`}
        orientation={field.labelAbove ? undefined : 'horizontal'}
      >
        <Input contentBefore={<Icon iconName="InsertSignatureLine" />} />
      </Field>
    </div>
  );

  const FileControl = (field: IField) => (
    <div style={controlWrapperStyle}>
      {renderFieldNameTag(field.name)}
      <Field
        label={`${field.label}`}
        orientation={field.labelAbove ? undefined : 'horizontal'}
      >
        <Input contentBefore={<Icon iconName="OpenFile" />} />
      </Field>
    </div>
  );

  const FileViewerControl = (field: IField) => (
    <div style={controlWrapperStyle}>
      {renderFieldNameTag(field.name)}
      <FileViewerField field={field} />
    </div>
  );

  // DroppingArea uses inPreviewMode prop directly, no local state
  const DroppingArea = (
    <div
      className={classes.droppingArea}
      style={{ display: inPreviewMode ? 'none' : undefined }}
    >
      <Text variant="small">Drag fields here</Text>
    </div>
  );

  const PanelControl = (
    field: IField,
    allFields: IField[],
    onRenderCell: (item?: IField, index?: number) => React.ReactElement
  ) => {
    const childFields =
      allFields.filter(
        (item) =>
          item.parentName === field.name &&
          (!hasTabs || item.tabName === field.tabName)
      ) || [];
    return (
      <div className={classes.panelContainer}>
        <div className={classes.panelHeader}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              width: '100%',
            }}
          >
            <Label>{field.label}</Label>
            <code
              style={{
                display: inPreviewMode ? 'none' : 'block',
                fontFamily: 'Consolas, "Courier New", monospace',
                fontSize: 11,
                color: tokens.colorNeutralForeground3,
              }}
            >
              {field.name}
            </code>
          </div>
        </div>
        <div
          style={{
            marginBottom: 4,
            padding: 5,
            display: field.isCollapsed === true ? 'none' : undefined,
          }}
        >
          <List items={childFields} onRenderCell={onRenderCell} />
          <Droppable id={`droppable-panel-${field.name}-end`}>
            {DroppingArea}
          </Droppable>
        </div>
      </div>
    );
  };

  const controlMap: {
    [key: string]: (
      field: IField,
      fields: IField[],
      onRenderCell: (item?: IField, index?: number) => React.ReactElement
    ) => React.ReactElement;
  } = {
    String: (field) => TextFieldControl(field),
    Number: (field) => TextFieldControl(field),
    NumberWithUnit: (field) => NumberWithUnitControl(field),
    Lookup: (field) => DropdownControl(field),
    Image: (field) => ImageControl(field),
    Date: (field) => DatePickerControl(field),
    AutoComplete: (field) => AutoCompleteControl(field),
    UserSelector: (field) => UserSelectorControl(field),
    Address: (field) => AddressControl(field),
    Coordinate: (field) => CoordinateControl(field),
    Panel: (field, fields, onRenderCell) =>
      PanelControl(field, fields, onRenderCell),
    RadioButton: (field) => RadioButtonControl(field),
    Checkbox: (field) => CheckboxControl(field),
    Text: (field) => TextControl(field),
    Signature: (field) => SignatureControl(field),
    File: (field) => FileControl(field),
    FileViewer: (field) => FileViewerControl(field),
    Contact: (field) => ContactControl(field),
  };

  const onRenderCell = React.useCallback(
    (item?: IField, _index?: number | undefined): React.ReactElement => {
      if (!item) return <></>;
      const control =
        item.fieldType === 'Panel'
          ? controlMap[item.fieldType](item, items, onRenderCell)
          : controlMap[item.fieldType](item, [], () => <></>);

      return (
        <>
          <Droppable
            id={`droppable-${item.parentName ? 'panel-' : ''}${item.name}`}
          >
            {DroppingArea}
          </Droppable>
          {inPreviewMode ? (
            control
          ) : (
            <FieldWrapper
              fieldName={item.name}
              onProperties={() => {
                const index = items.findIndex(
                  (i: IField) => i.name === item.name
                );
                setCurrFieldIndex(index);
                setSidePanel('FieldEditor');
              }}
              onDelete={() => {
                resetItemsAndMetadata(
                  items.filter(
                    (i: IField) =>
                      i.name !== item.name && i.parentName !== item.name
                  )
                );
              }}
              isPanel={item.fieldType === 'Panel'}
              onExpand={() => {
                item.isCollapsed = false;
                setItems([...items]);
              }}
              onCollapse={() => {
                item.isCollapsed = true;
                setItems([...items]);
              }}
              isCollapsed={!!item.isCollapsed}
            >
              {control}
            </FieldWrapper>
          )}
        </>
      );
    },
    [items]
  );

  const controls = [
    { key: 'String', text: 'Text Field', icon: 'TextField' },
    { key: 'Number', text: 'Number Field', icon: 'NumberField' },
    {
      key: 'NumberWithUnit',
      text: 'Number with Units',
      icon: 'NumberWithUnit',
    },
    { key: 'Lookup', text: 'Lookup', icon: 'Dropdown' },
    { key: 'Date', text: 'Date Picker', icon: 'Calendar' },
    { key: 'Image', text: 'Media', icon: 'FileImage' },
    { key: 'AutoComplete', text: 'AutoComplete', icon: 'AutoFillTemplate' },
    { key: 'Contact', text: 'Contact', icon: 'Phone' },
    { key: 'Address', text: 'Address', icon: 'MapPin' },
    { key: 'Coordinate', text: 'Coordinate', icon: 'POI' },
    { key: 'Panel', text: 'Panel', icon: 'ResponsesMenu' },
    { key: 'Checkbox', text: 'Checkbox', icon: 'CheckboxComposite' },
    { key: 'RadioButton', text: 'Radio button', icon: 'RadioBtnOn' },
    { key: 'Text', text: 'Text', icon: 'TextDocument' },
    { key: 'Signature', text: 'Signature', icon: 'InsertSignatureLine' },
    { key: 'File', text: 'File', icon: 'OpenFile' },
    { key: 'FileViewer', text: 'File Viewer', icon: 'DocumentSearch' },
    { key: 'UserSelector', text: 'User Selector', icon: 'Contact' },
  ];

  const [sidePanel, setSidePanel] = useState<
    | 'LookupEditor'
    | 'FieldEditor'
    | 'ValidationEditor'
    | 'PermissionEditor'
    | undefined
  >();

  const resolveDropTarget = (
    overId: string,
    list: IField[]
  ): {
    parentName?: string;
    tabName?: string;
    insertIndex: number;
  } | null => {
    if (overId.startsWith('droppable-end-tab-')) {
      const tabName = decodeURIComponent(
        overId.replace('droppable-end-tab-', '')
      );
      return { tabName, insertIndex: list.length };
    }

    if (overId === 'droppable-end') {
      return { insertIndex: list.length };
    }

    if (overId.startsWith('droppable-panel-')) {
      const dropTargetName = overId.replace('droppable-panel-', '');
      const droppedAtPanelEnd = dropTargetName.endsWith('-end');
      const targetFieldName = droppedAtPanelEnd
        ? dropTargetName.slice(0, -4)
        : dropTargetName;
      const targetField = list.find((item) => item.name === targetFieldName);
      const parentPanel =
        targetField?.fieldType === 'Panel'
          ? targetField
          : list.find(
              (item) =>
                item.fieldType === 'Panel' &&
                item.name === targetField?.parentName
            );

      if (!parentPanel) {
        return null;
      }

      if (droppedAtPanelEnd) {
        const insertIndex =
          list.findIndex((item) => item.name === parentPanel.name) +
          list.filter((item) => item.parentName === parentPanel.name).length +
          1;
        return {
          parentName: parentPanel.name,
          tabName: parentPanel.tabName,
          insertIndex,
        };
      }

      const insertIndex = list.findIndex(
        (item) => item.name === targetFieldName
      );
      if (insertIndex < 0) {
        return null;
      }
      return {
        parentName: parentPanel.name,
        tabName: parentPanel.tabName,
        insertIndex,
      };
    }

    const targetFieldName = overId.replace('droppable-', '');
    const targetFieldIndex = list.findIndex(
      (item) => item.name === targetFieldName
    );
    if (targetFieldIndex < 0) {
      return null;
    }
    const targetField = list[targetFieldIndex];
    return {
      parentName: targetField.parentName,
      tabName: targetField.parentName ? undefined : targetField.tabName,
      insertIndex: targetFieldIndex,
    };
  };

  const handleFieldMove = (movedName: string, overId: string) => {
    const moved = items.find((item) => item.name === movedName);
    if (!moved) {
      return;
    }

    // Gather the moved field plus its descendants so panels travel as a unit.
    const descendantNames = new Set<string>([moved.name]);
    if (moved.fieldType === 'Panel') {
      let grew = true;
      while (grew) {
        grew = false;
        for (const item of items) {
          if (
            item.parentName &&
            descendantNames.has(item.parentName) &&
            !descendantNames.has(item.name)
          ) {
            descendantNames.add(item.name);
            grew = true;
          }
        }
      }
    }

    const target = resolveDropTarget(overId, items);
    if (!target) {
      return;
    }

    // Disallow dropping a field onto itself or into its own subtree.
    if (target.parentName && descendantNames.has(target.parentName)) {
      return;
    }

    // Nested panels are only allowed inside repeatable panels.
    if (target.parentName) {
      const parentPanel = items.find((i) => i.name === target.parentName);
      if (
        moved.fieldType === 'Panel' &&
        !parentPanel?.repeatingMode?.startsWith('M') &&
        !parentPanel?.repeatingMode?.startsWith('E')
      ) {
        return;
      }
    }

    const block = items.filter((item) => descendantNames.has(item.name));
    const remaining = items.filter((item) => !descendantNames.has(item.name));

    // The destination index was computed against the full list; recompute it
    // against `remaining` using the field that currently sits at that index.
    let insertIndex = remaining.length;
    if (target.insertIndex < items.length) {
      const anchor = items[target.insertIndex];
      if (anchor && !descendantNames.has(anchor.name)) {
        const idx = remaining.findIndex((item) => item.name === anchor.name);
        if (idx >= 0) {
          insertIndex = idx;
        }
      }
    }

    const relocated: IField = {
      ...moved,
      parentName: target.parentName,
      tabName: target.tabName,
    };
    const relocatedBlock = block.map((item) =>
      item.name === moved.name ? relocated : item
    );

    const newItems = [...remaining];
    newItems.splice(insertIndex, 0, ...relocatedBlock);
    resetItemsAndMetadata(newItems);
  };

  const handleDragEnd = (event: any) => {
    const overId = event?.over?.id?.toString();
    if (!overId) {
      return;
    }

    const activeId = event?.active?.id?.toString() ?? '';
    if (activeId.startsWith(MOVE_DRAG_PREFIX)) {
      handleFieldMove(activeId.slice(MOVE_DRAG_PREFIX.length), overId);
      return;
    }

    const newField: IField = {
      name: `${event?.active.id}${
        items.filter((item) => item.fieldType === event?.active.id).length + 1
      }`,
      fieldType: event?.active.id,
      label: `${event?.active.id} ${
        items.filter((item) => item.fieldType === event?.active.id).length + 1
      }`,
    };

    const newItems = [...items];
    if (overId.startsWith('droppable-end-tab-')) {
      newField.tabName = decodeURIComponent(
        overId.replace('droppable-end-tab-', '')
      );
      newItems.splice(newItems.length, 0, newField);
    } else if (overId === 'droppable-end') {
      newItems.splice(newItems.length, 0, newField);
    } else if (overId.startsWith('droppable-panel-')) {
      const dropTargetName = overId.replace('droppable-panel-', '');
      const droppedAtPanelEnd = dropTargetName.endsWith('-end');
      const targetFieldName = droppedAtPanelEnd
        ? dropTargetName.slice(0, -4)
        : dropTargetName;
      const targetField = newItems.find(
        (item) => item.name === targetFieldName
      );
      const parentPanel =
        targetField?.fieldType === 'Panel'
          ? targetField
          : newItems.find(
              (item) =>
                item.fieldType === 'Panel' &&
                item.name === targetField?.parentName
            );

      if (!parentPanel) {
        return;
      }

      // Nested panels are only allowed inside repeatable panels.
      if (
        newField.fieldType === 'Panel' &&
        !parentPanel.repeatingMode?.startsWith('M') &&
        !parentPanel.repeatingMode?.startsWith('E')
      ) {
        return;
      }

      newField.parentName = parentPanel.name;
      newField.tabName = parentPanel.tabName;
      newField.name = `${parentPanel.name}-${event?.active.id}${
        items.filter((item) => item.fieldType === event?.active.id).length + 1
      }`;
      newField.label = `${event?.active.id} ${
        items.filter((item) => item.fieldType === event?.active.id).length + 1
      }`;

      if (droppedAtPanelEnd) {
        newItems.splice(
          items.findIndex((item) => item.name === parentPanel.name) +
            items.filter((item) => item.parentName === parentPanel.name)
              .length +
            1,
          0,
          newField
        );
      } else {
        const insertIndex = items.findIndex(
          (item) => item.name === targetFieldName
        );
        if (insertIndex < 0) {
          return;
        }

        newItems.splice(insertIndex, 0, newField);
      }
    } else {
      const targetFieldName = overId.replace('droppable-', '');
      const targetFieldIndex = newItems.findIndex(
        (item) => item.name === targetFieldName
      );

      if (targetFieldIndex < 0) {
        return;
      }

      const targetField = newItems[targetFieldIndex];
      if (!targetField.parentName) {
        newField.tabName = targetField.tabName;
      }

      newItems.splice(targetFieldIndex, 0, newField);
    }
    resetItemsAndMetadata(newItems);
  };

  const handleDragStart = (event: any) => {
    setActiveId(event?.active?.id ?? null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const resetItemsAndMetadata = (newItems: IField[]) => {
    setItems(newItems);
    setMetadata &&
      setMetadata({
        ...metadata,
        fields: newItems,
        lookups: metadata?.lookups || [],
        validations: metadata?.validations || [],
        interactions: metadata?.interactions || [],
        tabs: metadata?.tabs,
        cardLayout: metadata?.cardLayout,
      });
    setMetadataStr &&
      setMetadataStr(
        JSON.stringify({ ...metadata, fields: newItems }, null, 2)
      );
  };

  const onRenderControlCell = React.useCallback(
    (c?: any, _index?: number | undefined): React.ReactElement | null => {
      if (!c) return <></>;
      if (activeId === c.key) return null;

      return (
        <Draggable id={c.key} key={c.key} activeId={activeId}>
          <ActionButton
            title={c.text}
            ariaLabel={c.text}
            styles={{
              root: {
                height: 32,
                width: '196px',
                maxWidth: '100%',
                padding: '0 8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                textAlign: 'left',
                borderRadius: 5,
                border: `1px solid ${tokens.colorNeutralStroke2}`,
                cursor: 'grab',
              },
              flexContainer: {
                justifyContent: 'flex-start',
                alignItems: 'center',
                width: '100%',
              },
            }}
          >
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 8 }}
            >
              <Icon
                iconName="GripperDotsVertical"
                styles={{
                  root: { fontSize: 14, color: tokens.colorNeutralForeground3 },
                }}
              />
              <Icon iconName={c.icon} styles={{ root: { fontSize: 16 } }} />
              <span style={{ fontSize: 14 }}>{c.text}</span>
            </Stack>
          </ActionButton>
        </Draggable>
      );
    },
    [items, activeId]
  );

  const activeTabName = hasTabs
    ? selectedTab || metadata?.tabs?.[0]?.tabName || ''
    : '';

  const activeTabItems = hasTabs
    ? items.filter((item) => item.tabName === activeTabName)
    : items;

  const onTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab((data.value as string) ?? '');
  };

  return (
    <>
      {inPreviewMode && (
        <MessageBar intent="warning">
          <MessageBarBody>Preview Mode</MessageBarBody>
          <MessageBarActions
            containerAction={
              <Button
                aria-label="dismiss"
                appearance="transparent"
                icon={<DismissRegular />}
                onClick={() => {
                  setInPreviewMode && setInPreviewMode(false);
                }}
              />
            }
          ></MessageBarActions>
        </MessageBar>
      )}
      <DndContext
        onDragEnd={(e) => {
          setActiveId(null);
          setTimeout(() => handleDragEnd(e), 0);
        }}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 10,
            alignContent: 'center',
            marginBottom: 10,
          }}
        >
          <div
            style={{
              display: inPreviewMode ? 'none' : 'flex',
              flexDirection: 'column',
              gap: 5,
            }}
          >
            <List items={controls} onRenderCell={onRenderControlCell} />
          </div>

          {hasTabs ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                width: '500px',
                maxWidth: '500px',
              }}
            >
              <div
                style={{
                  width: '500px',
                  maxWidth: '500px',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                }}
              >
                <TabList
                  selectedValue={activeTabName}
                  onTabSelect={onTabSelect}
                  style={{ minWidth: 'max-content' }}
                >
                  {(metadata?.tabs ?? []).map((tab) => (
                    <Tab key={tab.tabName} value={tab.tabName}>
                      {tab.caption}
                    </Tab>
                  ))}
                </TabList>
              </div>

              <div className={classes.canvas}>
                <div className={classes.canvasInner}>
                  <List
                    items={activeTabItems.filter((item) => !item.parentName)}
                    onRenderCell={onRenderCell}
                  />
                  <Droppable
                    id={`droppable-end-tab-${encodeURIComponent(
                      activeTabName
                    )}`}
                  >
                    {DroppingArea}
                  </Droppable>
                </div>
              </div>
            </div>
          ) : (
            <div className={classes.canvas}>
              <div className={classes.canvasInner}>
                <List
                  items={items.filter((item) => !item.parentName)}
                  onRenderCell={onRenderCell}
                />
                <Droppable id={`droppable-end`}>{DroppingArea}</Droppable>
              </div>
            </div>
          )}
        </div>

        <DragOverlay dropAnimation={{ duration: 0 }}>
          {activeId ? (
            <div style={{ pointerEvents: 'none' }}>
              <ActionButton
                styles={{
                  root: {
                    height: 32,
                    width: 196,
                    padding: '0 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    borderRadius: 5,
                    background: 'rgba(255, 255, 255, 0.6)',
                    border: `1px solid ${tokens.colorNeutralStroke1}`,
                    boxShadow: tokens.shadow8,
                    cursor: 'grabbing',
                  },
                  rootHovered: {
                    background: 'rgba(255, 255, 255, 0.6)',
                  },
                }}
              >
                <Stack
                  horizontal
                  verticalAlign="center"
                  tokens={{ childrenGap: 8 }}
                >
                  {activeId.startsWith(MOVE_DRAG_PREFIX) ? (
                    <>
                      <Icon
                        iconName="Move"
                        styles={{
                          root: {
                            fontSize: 14,
                            color: tokens.colorNeutralForeground3,
                          },
                        }}
                      />
                      <span style={{ fontSize: 14 }}>
                        {(() => {
                          const movedName = activeId.slice(
                            MOVE_DRAG_PREFIX.length
                          );
                          const moved = items.find((i) => i.name === movedName);
                          return moved?.label || moved?.name || 'Field';
                        })()}
                      </span>
                    </>
                  ) : (
                    <>
                      <Icon
                        iconName="GripperDotsVertical"
                        styles={{
                          root: {
                            fontSize: 14,
                            color: tokens.colorNeutralForeground3,
                          },
                        }}
                      />
                      <Icon
                        iconName={
                          controls.find((c) => c.key === activeId)?.icon
                        }
                        styles={{ root: { fontSize: 16 } }}
                      />
                      <span style={{ fontSize: 14 }}>
                        {controls.find((c) => c.key === activeId)?.text}
                      </span>
                    </>
                  )}
                </Stack>
              </ActionButton>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      <Panel
        isOpen={sidePanel !== undefined}
        onDismiss={() => setSidePanel(undefined)}
        closeButtonAriaLabel="Close"
        type={PanelType.medium}
        headerText={sidePanel}
      >
        <FieldEditorV2
          metadata={metadata}
          setMetadata={setMetadata}
          setMetadataStr={setMetadataStr}
          references={references}
          entityType={props.entityType}
          currFieldIndex={currFieldIndex}
          setCurrFieldIndex={setCurrFieldIndex}
          setSidePanel={setSidePanel}
        />
      </Panel>
    </>
  );
};

export default FieldsEditorV2;
